/* global BigInt */

export function fromWei(value, web3) {
  return web3.utils.fromWei(String(value), 'ether');
}

export function toWei(value, web3) {
  return web3.utils.toWei(String(value), 'ether');
}

/**
 * Compare two wei-denominated values as BigInts.
 * web3 v4 removed `utils.toBN`, so we use native BigInt instead.
 * Returns true when `a` is strictly less than `b`.
 */
export function isWeiLessThan(a, b) {
  try {
    return BigInt(String(a)) < BigInt(String(b));
  } catch {
    return Number(a) < Number(b);
  }
}

export function formatTokens(value, decimals = 2) {
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  }).format(num);
}

/**
 * Format a wallet balance (token or native ETH) for display.
 * Trims trailing zeros and groups thousands, but keeps real precision so small
 * holdings aren't rounded down to a misleading "0" — any non-zero amount below
 * `dustThreshold` is shown as e.g. "< 0.0001" instead.
 */
export function formatBalance(value, { maxDecimals = 6, dustThreshold = 0.0001 } = {}) {
  const num = Number(value);
  if (!Number.isFinite(num)) return '0';
  if (num === 0) return '0';
  if (num > 0 && num < dustThreshold) return `< ${dustThreshold}`;
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDecimals,
  }).format(num);
}

export function formatPercentFromBps(bps) {
  const num = Number(bps) / 100;
  if (!Number.isFinite(num)) return '—';
  return `${num.toFixed(2)}%`;
}

export function formatDuration(seconds) {
  const total = Math.max(0, Math.floor(Number(seconds)));
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

export const MAX_SUPPLY = 1_000_000_000;

export function percentOfSupply(staked, supply) {
  const stakedNum = Number(staked);
  const supplyNum = Number(supply);
  if (!supplyNum || supplyNum <= 0) return 0;
  return (stakedNum / supplyNum) * 100;
}

/** Tokens burned via in-platform redemptions (max cap minus current on-chain supply). */
export function burnedFromMaxSupply(currentSupply) {
  const current = Number(currentSupply);
  if (!Number.isFinite(current)) return 0;
  return Math.max(0, MAX_SUPPLY - current);
}
